import { Document } from 'iconsax-react';
import { useMemo } from 'react';
import VideoViewer from '@/components/common/media-content/video-viewer';
import { SafeImage } from '@/components/common/safe-image';
import usePreviewImage from '@/hooks/files/use-preview-image';
import useUserPlatform from '@/hooks/use-user-platform';
import type { Uploadable } from '@/models/uploadable';
import { FileType } from '@/utils/file-type';
import cn from '@/utils/merge-class-names';

export interface FilePreviewProps {
  fileType: FileType;
  file: Uploadable | null | undefined;
  className?: string;
  onClick?: () => void;
}

export function FilePreview({ file, fileType, className, onClick }: FilePreviewProps) {
  const { isIOS } = useUserPlatform();
  const [preview] = usePreviewImage(file?.url);

  const previewableFileTypes = useMemo(() => [FileType.Image, FileType.Video], []);

  if (previewableFileTypes.includes(fileType) && !preview) return null;

  switch (fileType) {
    case FileType.Image:
      return (
        <SafeImage
          displayType='img'
          src={preview}
          className={cn('mx-auto aspect-square w-full rounded', className)}
          onClick={onClick}
        />
      );
    case FileType.Video:
      return (
        <VideoViewer
          controls={false}
          poster={false}
          src={preview}
          autoPlay={isIOS}
          className={cn('mx-auto aspect-auto size-auto min-h-16 max-h-48 rounded', className)}
        />
      );
    default:
      return (
        <div className={cn('mx-auto flex h-full items-center gap-4 rounded bg-neutral-200 px-4', className)}>
          <Document className='size-4' />

          <div className='flex-1 break-all'>{file?.name}</div>
        </div>
      );
  }
}
